import { FDRStatus } from '@cibo/core'
import {
  PaginatedFieldsProvider,
  ReadOnlyPaginatedFieldsTable,
  fieldEnrollmentColumn,
  fieldGrowerColumn,
  fieldIdentifierColumn,
  fieldUpdatedAtColumn,
} from '@cibo/landmanager'
import {
  Loading,
  PaginatedUsersTable,
  useOrgUserColumns,
  useOrgUserFdrStatsColumn,
  useOrganizations,
} from '@cibo/profile'
import { TabPanel, Tabs } from '@cibo/ui'
import { Box, Tab } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'
import { ADMIN_ROUTES } from 'navigation/types'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FDRRollups } from '../FDRRollups'
import { ProgramOrganizationList } from './Organization'

export const ProgramDetail = () => {
  const { t } = useTranslation('impact/pages/Amdin/ProgramDetail')
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const organizations = useOrganizations()
  const [params, setParams] = useSearchParams({ t: 'organizations' })

  const allUserColumns = useOrgUserColumns({})
  const enrolledColumn = useOrgUserFdrStatsColumn({ status: FDRStatus.enrolled })
  const enrollingColumn = useOrgUserFdrStatsColumn({ status: FDRStatus.enrolling })
  const eligibleColumn = useOrgUserFdrStatsColumn({ status: FDRStatus.eligibleForEnrollment })
  const userColumns = useMemo(
    () => [
      allUserColumns.name,
      allUserColumns.email,
      allUserColumns.organization,
      enrolledColumn,
      enrollingColumn,
      eligibleColumn,
      allUserColumns.lastLoginDate,
    ],
    [allUserColumns]
  )

  const fieldColumns = useMemo(() => {
    return [
      fieldIdentifierColumn({ t, flex: 3 }),
      fieldGrowerColumn({ t, width: 150 }),
      fieldEnrollmentColumn({ t, programId: `${programId}`, width: 150 }),
      fieldUpdatedAtColumn({ t, width: 150, flex: 0 }),
    ].filter(c => !!c) as GridColDef[]
  }, [])

  const showOrganizations = !!organizations.data?.numAvailable
  useEffect(() => {
    if (!organizations.isLoading && !showOrganizations && params.get('t') === 'organizations') {
      setParams({ t: 'users' })
    }
  }, [showOrganizations, organizations.isLoading])

  if (organizations.isLoading) {
    return <Loading />
  }

  return (
    <>
      <Box>
        <FDRRollups programId={programId} />
      </Box>
      <Tabs
        value={params.get('t')}
        onChange={(event, newTab) => setParams({ t: newTab }, { replace: true })}
      >
        {!!showOrganizations && (
          <Tab value={'organizations'} label={t('tab', { context: 'organizations' })} />
        )}
        <Tab value={'users'} label={t('tab', { context: 'users' })} />
        <Tab value={'fields'} label={t('tab', { context: 'fields' })} />
      </Tabs>
      {!!showOrganizations && (
        <TabPanel currentValue={params.get('t')} value={'organizations'}>
          <ProgramOrganizationList />
        </TabPanel>
      )}
      <TabPanel currentValue={params.get('t')} value={'users'}>
        <PaginatedUsersTable
          availableFilters={{
            persona: true,
            organization: true,
            includeInactive: true,
            state: true,
            county: true,
            acres: true,
          }}
          headerPaletteColor="primary"
          baseQuery={{
            fdrStats: [
              { programId, status: FDRStatus.enrolled },
              { programId, status: FDRStatus.enrolling },
              { programId, status: FDRStatus.eligibleForEnrollment },
            ],
          }}
          columns={userColumns}
          onRowClick={({ id }) =>
            navigate(generatePath(ADMIN_ROUTES.USER_DETAIL, { userId: id as string }))
          }
          goToUser={() => null}
        />
      </TabPanel>
      <TabPanel currentValue={params.get('t')} value={'fields'}>
        <PaginatedFieldsProvider>
          <ReadOnlyPaginatedFieldsTable
            density="compact"
            headerPaletteColor="primary"
            getRowHeight={() => 'auto'}
            columns={fieldColumns}
            baseQuery={{
              fdrStatusAllOf: [{ programId, status: FDRStatus.enrolled }],
            }}
          />
        </PaginatedFieldsProvider>
      </TabPanel>
    </>
  )
}
